/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { memo } from "react";
import type { IconProps } from "./types";

/** Left chevron arrow — used in image/video carousels */
export const ChevronLeftIcon: React.FC<IconProps> = memo(
  ({ width = "24", height = "24", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      className={className}
    >
      <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
    </svg>
  ),
);
ChevronLeftIcon.displayName = "ChevronLeftIcon";

/** Right chevron arrow — used in image/video carousels */
export const ChevronRightIcon: React.FC<IconProps> = memo(
  ({ width = "24", height = "24", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      className={className}
    >
      <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
    </svg>
  ),
);
ChevronRightIcon.displayName = "ChevronRightIcon";

/** Right arrow icon — used for "next page" pagination */
export const NextPageIcon: React.FC<IconProps> = memo(
  ({ width = "10", height = "10", className }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 512 512"
      width={width}
      height={height}
    >
      <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z" />
    </svg>
  ),
);
NextPageIcon.displayName = "NextPageIcon";

/** Back arrow for mobile menu — narrow left chevron */
export const MenuBackIcon: React.FC<IconProps> = memo(
  ({ width = "10", height = "17", className }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      width={width}
      height={height}
      viewBox="0 0 10 17"
      fill="none"
    >
      <path d="M9 1L2 8.24138L9 16" stroke="white" strokeWidth="1.75" />
    </svg>
  ),
);
MenuBackIcon.displayName = "MenuBackIcon";

/** Small dropdown chevron for form selects */
export const DropdownChevronIcon: React.FC<
  IconProps & { style?: React.CSSProperties }
> = ({ width = "12", height = "8", style, className }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 12 8"
    fill="none"
    style={style}
    className={className}
  >
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="#535353"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** FontAwesome-style chevron-right icon (used in article sidebar navigation) */
export const ChevronRightFaIcon: React.FC<IconProps> = memo(
  ({ className = "svg-inline--fa fa-chevron-right" }) => (
    <svg
      aria-hidden="true"
      focusable="false"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 320 512"
    >
      <path
        fill="currentColor"
        d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"
      />
    </svg>
  ),
);
ChevronRightFaIcon.displayName = "ChevronRightFaIcon";

/** FontAwesome-style angle-down icon (used in product dropdown navigation) */
export const AngleDownFaIcon: React.FC<IconProps> = memo(
  ({ className = "svg-inline--fa fa-angle-down down-arrow" }) => (
    <svg
      className={className}
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 448 512"
    >
      <desc>Down arrow</desc>
      <path
        fill="currentColor"
        d="M201.4 342.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 274.7 86.6 137.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
      />
    </svg>
  ),
);
AngleDownFaIcon.displayName = "AngleDownFaIcon";
